import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Search, Tag, RefreshCw, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RateCard() {
  const { authHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);

  // Inline edit state
  const [editId,   setEditId]   = useState(null);
  const [editRate, setEditRate] = useState('');
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    axios.get(`${API}/customers`, { headers: authHeader() })
      .then(r => setCustomers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search)
  );

  const startEdit = (c) => {
    setEditId(c.id);
    setEditRate(c.current_rate > 0 ? String(c.current_rate) : '');
  };

  const cancelEdit = () => { setEditId(null); setEditRate(''); };

  const saveRate = async (customerId) => {
    const newRate = parseFloat(editRate) || 0;
    setSaving(true);
    try {
      await axios.patch(`${API}/customers/${customerId}`, { current_rate: newRate }, { headers: authHeader() });
      setCustomers(cs => cs.map(c => c.id === customerId ? { ...c, current_rate: newRate } : c));
      setEditId(null);
    } catch (err) {
      console.error('Rate update failed', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-1 gap-4 flex-wrap">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
            <Tag size={11} /> Rate Card
          </div>
          <h1 className="font-black text-3xl tracking-tight">Rate Card</h1>
          <p className="text-sm text-gray-500 mt-1">Set ₹/kg rate per customer — auto-fills during Gas Sale entry</p>
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            className="pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm w-52 focus:outline-none focus:border-gray-400 transition-colors bg-white"
            placeholder="Search customer..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-5">Click "Edit rate" to update. Changes apply to new sales immediately.</p>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 small-label text-gray-500 min-w-[220px]">Customer</th>
                <th className="text-left px-4 py-3 small-label text-gray-500">Phone</th>
                <th className="text-left px-4 py-3 small-label text-gray-500">Type</th>
                <th className="text-right px-4 py-3 small-label text-gray-900 whitespace-nowrap">
                  Rate ₹/kg
                  <span className="ml-1 text-[9px] text-orange-500 font-bold">●</span>
                </th>
                <th className="text-left px-4 py-3 small-label text-gray-500 w-40">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-10">
                    <RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-400 text-[13px]">
                    No customers found. Add customers via Customers &amp; Vendors.
                  </td>
                </tr>
              ) : filtered.map(c => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900 text-[13px]">{c.name}</div>
                    {c.gstin && <div className="text-xs text-gray-400 mt-0.5">GSTIN: {c.gstin}</div>}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-500">{c.phone || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600 uppercase">
                      {c.customer_type || 'customer'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editId === c.id ? (
                      <input
                        autoFocus
                        type="number" min="0" step="0.01"
                        className="w-24 px-2 py-1 border border-blue-400 rounded text-[13px] text-right focus:outline-none focus:ring-1 focus:ring-blue-400"
                        value={editRate}
                        onChange={e => setEditRate(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveRate(c.id); if (e.key === 'Escape') cancelEdit(); }}
                        data-testid={`rate-input-${c.id}`}
                      />
                    ) : (
                      <div className="font-semibold text-[13px] text-gray-900">
                        {c.current_rate > 0 ? `₹${c.current_rate.toFixed(2)}` : <span className="text-gray-400">Not set</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editId === c.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveRate(c.id)}
                          disabled={saving}
                          className="flex items-center gap-1 text-[12px] text-green-700 hover:text-green-900 font-medium transition-colors"
                          data-testid={`save-rate-btn-${c.id}`}
                        >
                          {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />} Save
                        </button>
                        <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-700 transition-colors">
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(c)}
                        className="text-[12px] text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        data-testid={`edit-rate-btn-${c.id}`}
                      >
                        Edit rate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
          {filtered.length} customers
        </div>
      </div>
    </div>
  );
}
